// ✅ ColisageSuivi.jsx – Page d'information sur le colisage et le suivi
import React, { useMemo, useState } from "react";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";

import { db } from "../firebase";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(d);
  }
}

export default function ColisageSuivi() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState("");
  const [data, setData] = useState(null); // { colis, articles[], lots[] }

  const totalArticles = useMemo(() => data?.articles?.length || 0, [data]);
  const totalUnits = useMemo(
    () => (data?.articles || []).reduce((s, a) => s + Number(a.qty || 0), 0),
    [data]
  );

  const lotsByStep = useMemo(() => {
    const by = { warehouse: [], container: [], lome: [] };
    (data?.lots || []).forEach((l) => {
      by[l.step]?.push(l);
    });
    // tri par date
    for (const k of Object.keys(by)) {
      by[k].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    }
    return by;
  }, [data]);

  const containerInfos = useMemo(() => {
    return (lotsByStep.container || [])
      .filter((l) => l.containerNo || l.shippingUrl)
      .map((l) => ({
        containerNo: l.containerNo || "",
        shippingUrl: l.shippingUrl || "",
        date: l.date || "",
      }));
  }, [lotsByStep]);

  const handleSearch = async () => {
    setNotFound("");
    setData(null);
    const codeTrim = code.trim();
    if (!codeTrim) return;

    try {
      setLoading(true);
      // 1) Trouver le colis par son code
      const q = query(
        collection(db, "colischine"),
        where("code", "==", codeTrim),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setNotFound("Aucun colis ne correspond à ce code.");
        return;
      }
      const doc0 = snap.docs[0];
      const colis = { id: doc0.id, ...doc0.data() };

      // 2) Charger les sous-collections
      const [artsSnap, lotsSnap] = await Promise.all([
        getDocs(collection(db, "colischine", doc0.id, "articles")),
        getDocs(collection(db, "colischine", doc0.id, "lots")),
      ]);
      const articles = artsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const lots = lotsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setData({ colis, articles, lots });
    } catch (e) {
      console.error(e);
      setNotFound("Erreur lors de la recherche. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div>
      <MenuNav />
      <MobileMenu />

      {/* Suivi de colis */}
      <section className="bg-track ocean-anim py-5">
        <span className="ocean-sparkles" aria-hidden="true"></span>

        <div className="container text-center position-relative">
          <h5 className="hero-subtitle">LEADER IN</h5>
          <h1 className="hero-title">SHIPPING & LOGISTICS</h1>
          <h3 className="mb-4 text-white">🔎 Suivre un colis</h3>
          <p className="text-white-50">
            Entrez votre numéro de colis pour connaître sa position actuelle.
          </p>

          <div className="d-flex justify-content-center mt-3">
            <input
              type="text"
              className="form-control w-50 me-2"
              placeholder="Ex: COL-20251005-ABCD"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onEnter}
            />
            <button className="btn btn-success" onClick={handleSearch} disabled={loading}>
              {loading ? "Recherche…" : "Suivre"}
            </button>
          </div>

          <p className="text-white-50 mt-3">
            * Vous verrez les étapes de traitement, les dates, et (si disponible) le tracking
            conteneur.
          </p>
          {notFound && <div className="alert alert-warning mt-3 d-inline-block">{notFound}</div>}
        </div>
      </section>

      {/* Résultat */}
      {data && (
        <section className="py-4">
          <div className="container">
            {/* Résumé colis */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <img
                    src={data.colis.cover || "https://via.placeholder.com/120x80?text=Colis"}
                    alt="cover"
                    width={120}
                    height={80}
                    className="rounded border"
                    style={{ objectFit: "cover" }}
                  />
                  <div className="flex-grow-1">
                    <h5 className="mb-1">
                      {data.colis.code} — {data.colis.clientName || "Client"}
                    </h5>
                    <div className="text-muted">
                      {data.colis.origin} → {data.colis.destination} • Statut:{" "}
                      <strong>{data.colis.status}</strong>
                    </div>
                    <div className="small">
                      Payé:{" "}
                      <strong>
                        {Number(data.colis.paidTotalXOF || 0).toLocaleString("fr-FR")} /{" "}
                        {Number(data.colis.estimateXOF || 0).toLocaleString("fr-FR")} XOF
                      </strong>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-semibold">Articles</div>
                    <div className="small">
                      Lignes: <strong>{totalArticles}</strong>
                      {" · "}Quantités: <strong>{totalUnits}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Étapes (timeline simple par colonnes) */}
            <div className="row g-3">
              {/* Entrepôt */}
              <div className="col-md-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h6 className="card-title d-flex align-items-center gap-2">
                      🏭 Entrepôt (Chine)
                      <span className="badge bg-secondary">
                        {(lotsByStep.warehouse || []).reduce((s, l) => s + Number(l.qty || 0), 0)} / {totalUnits}
                      </span>
                    </h6>
                    <ul className="list-unstyled mb-0">
                      {(lotsByStep.warehouse || []).length === 0 && (
                        <li className="text-muted">Aucune entrée.</li>
                      )}
                      {(lotsByStep.warehouse || []).map((l) => (
                        <li key={l.id} className="mb-2">
                          <div className="small">
                            <span className="fw-semibold">+{l.qty}</span>{" "}
                            <span className="text-muted">• {formatDate(l.date)}</span>
                          </div>
                          {l.note && <div className="small text-muted">{l.note}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Conteneur */}
              <div className="col-md-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h6 className="card-title d-flex align-items-center gap-2">
                      🚢 Mise en conteneur
                      <span className="badge bg-primary">
                        {(lotsByStep.container || []).reduce((s, l) => s + Number(l.qty || 0), 0)} / {totalUnits}
                      </span>
                    </h6>
                    <ul className="list-unstyled mb-0">
                      {(lotsByStep.container || []).length === 0 && (
                        <li className="text-muted">Aucune entrée.</li>
                      )}
                      {(lotsByStep.container || []).map((l) => (
                        <li key={l.id} className="mb-2">
                          <div className="small">
                            <span className="fw-semibold">+{l.qty}</span>{" "}
                            <span className="text-muted">• {formatDate(l.date)}</span>
                          </div>
                          {(l.containerNo || l.shippingUrl) && (
                            <div className="small">
                              {l.containerNo && (
                                <span className="me-2">
                                  N° conteneur: <strong>{l.containerNo}</strong>
                                </span>
                              )}
                              {l.shippingUrl && (
                                <a href={l.shippingUrl} target="_blank" rel="noopener noreferrer">
                                  Site de shipping
                                </a>
                              )}
                            </div>
                          )}
                          {l.note && <div className="small text-muted">{l.note}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Réception à Lomé */}
              <div className="col-md-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h6 className="card-title d-flex align-items-center gap-2">
                      📦 Réception à Lomé
                      <span className="badge bg-success">
                        {(lotsByStep.lome || []).reduce((s, l) => s + Number(l.qty || 0), 0)} / {totalUnits}
                      </span>
                    </h6>
                    <ul className="list-unstyled mb-0">
                      {(lotsByStep.lome || []).length === 0 && (
                        <li className="text-muted">Aucune entrée.</li>
                      )}
                      {(lotsByStep.lome || []).map((l) => (
                        <li key={l.id} className="mb-2">
                          <div className="small">
                            <span className="fw-semibold">+{l.qty}</span>{" "}
                            <span className="text-muted">• {formatDate(l.date)}</span>
                          </div>
                          {l.note && <div className="small text-muted">{l.note}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Tracking conteneur (récap si plusieurs) */}
            {containerInfos.length > 0 && (
              <div className="card shadow-sm mt-3">
                <div className="card-body">
                  <h6 className="card-title">🔗 Tracking conteneur</h6>
                  <ul className="mb-0">
                    {containerInfos.map((c, i) => (
                      <li key={i} className="small">
                        {c.containerNo && (
                          <span className="me-2">
                            N° <strong>{c.containerNo}</strong>
                          </span>
                        )}
                        {c.shippingUrl ? (
                          <a href={c.shippingUrl} target="_blank" rel="noopener noreferrer">
                            Ouvrir le site de shipping
                          </a>
                        ) : (
                          <span className="text-muted">Lien non fourni</span>
                        )}
                        {" · "}
                        <span className="text-muted">{formatDate(c.date)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Hero (présentation) */}
      {!data && (
        <>
          <section className="bg-success text-white text-center py-5">
            <div className="container">
              <h1 className="display-5 fw-bold">Colisage & Suivi de Commande</h1>
              <p className="lead">
                Nous assurons une livraison fiable, suivie et sécurisée de vos colis à travers le Togo et au-delà.
              </p>
            </div>
          </section>

          <section className="py-5">
            <div className="container">
              <h3 className="mb-4">🚚 Étapes du Colisage</h3>
              <div className="row">
                <div className="col-md-6 mb-4">
                  <h5>1. Enregistrement du Colis</h5>
                  <p>Chaque colis est enregistré avec les informations de l’expéditeur, du destinataire, et le contenu.</p>
                </div>
                <div className="col-md-6 mb-4">
                  <h5>2. Attribution d’un Code de Suivi</h5>
                  <p>Un code unique est généré pour permettre le suivi en temps réel.</p>
                </div>
                <div className="col-md-6 mb-4">
                  <h5>3. Emballage Sécurisé</h5>
                  <p>Emballage professionnel et sécurisé pour protéger vos biens.</p>
                </div>
                <div className="col-md-6 mb-4">
                  <h5>4. Transport & Livraison</h5>
                  <p>Livraison dans des conditions optimales à l’adresse indiquée.</p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Contact rapide */}
      <section className="bg-dark text-white py-4">
        <div className="container text-center">
          <p className="mb-2">Une question ou un problème avec votre colis ?</p>
          <h5>📞 Contactez-nous au +228 92 07 60 72 / -- -- -- --</h5>
        </div>
      </section>
    </div>
  );
}
